import { toIsoDate } from '../../core/utilities/dates'
import { AliasBinding } from '../decorators/alias'
import { Mappable } from '../decorators/mapped'

export type DynamicBinding = Record<string, unknown>

export class SerialisationConfig {
  constructor(
    public creator: () => Deserialisable | Mappable,
    public fields?: Record<string, SerialisationConfig>
  ) {}

  createInstance(): Deserialisable | Mappable {
    return this.creator()
  }
}

export class Deserialisable {
  public static serialisationConfig: SerialisationConfig =
    new SerialisationConfig(() => new Deserialisable())

  public static deserialise<T>(data: unknown, config: SerialisationConfig): T {
    const obj = config.createInstance()
    obj.update(data, config)
    return obj as T
  }

  public static deserialiseList<T>(
    data: unknown[],
    config: SerialisationConfig
  ): T[] {
    const list: T[] = []
    data.forEach((item) => {
      list.push(this.deserialise<T>(item, config))
    })
    return list
  }

  constructor(protected __alias?: AliasBinding) {}

  getAlias(): AliasBinding | undefined {
    return this.__alias
  }

  update(attrs: unknown, config: SerialisationConfig) {
    for (const attr of Object.keys(attrs as DynamicBinding)) {
      if (Object.prototype.hasOwnProperty.call(attrs, attr)) {
        const name = Object.prototype.hasOwnProperty.call(this.__alias, attr)
          ? (this.__alias as AliasBinding)[attr]
          : attr
        const value = (attrs as DynamicBinding)[attr]
        if (!(value instanceof Object)) {
          if (typeof value === 'string') {
            ;(this as DynamicBinding)[name] = toIsoDate(value) ?? value
          } else {
            ;(this as DynamicBinding)[name] = value
          }
        } else {
          if (
            config.fields != null &&
            Object.prototype.hasOwnProperty.call(config.fields, name)
          ) {
            if (value instanceof Array) {
              ;(this as DynamicBinding)[name] = Deserialisable.deserialiseList(
                value,
                config.fields[name]
              )
            } else {
              ;(this as DynamicBinding)[name] = Deserialisable.deserialise(
                value,
                config.fields[name]
              )
            }
          } else {
            ;(this as DynamicBinding)[name] = value
          }
        }
      }
    }
    return this
  }
}

export class Serialisable extends Deserialisable {
  constructor(__alias?: AliasBinding) {
    super(__alias)
  }

  serialise(): object {
    const data: DynamicBinding = {}
    for (const attr in this.__alias) {
      if (Object.prototype.hasOwnProperty.call(this.__alias, attr)) {
        data[attr] = this.serialiseField(
          (this as DynamicBinding)[this.__alias[attr]]
        )
      }
    }
    return data
  }

  private serialiseField(value: unknown): unknown {
    if (value instanceof Serialisable) {
      return value.serialise()
    } else if (value instanceof Array) {
      const collection = []
      for (const index in value) {
        if (Object.prototype.hasOwnProperty.call(value, index)) {
          collection.push(this.serialiseField(value[index]))
        }
      }
      return collection
    } else if (value instanceof Date) {
      return value.toISOString()
    } else {
      return value
    }
  }
}
